export type DashboardAuth = {
  token: string;
  brokerageId: string | null;
  agentId: string | null;
  role: 'broker' | 'agent';
};

export interface AuthStorage {
  load(): DashboardAuth | null;
  save(auth: DashboardAuth): void;
  clear(): void;
}

const STORAGE_KEY = 'field-ops.dashboard.auth';

const isDashboardAuth = (value: unknown): value is DashboardAuth => {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.token === 'string' &&
    (candidate.brokerageId === null || typeof candidate.brokerageId === 'string') &&
    (candidate.agentId === null || typeof candidate.agentId === 'string') &&
    (candidate.role === 'broker' || candidate.role === 'agent')
  );
};

const getBrowserLocalStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;

  try {
    const storage = window.localStorage;
    return storage &&
      typeof storage.getItem === 'function' &&
      typeof storage.setItem === 'function' &&
      typeof storage.removeItem === 'function'
      ? storage
      : null;
  } catch {
    return null;
  }
};

// localStorage-backed adapter. Guarded for environments without a usable Storage
// API — falls back to a no-op store rather than throwing.
export const createLocalStorageAuthStorage = (): AuthStorage => {
  return {
    load: () => {
      const storage = getBrowserLocalStorage();
      if (!storage) return null;

      const raw = storage.getItem(STORAGE_KEY);
      if (!raw) return null;
      try {
        const parsed: unknown = JSON.parse(raw);
        return isDashboardAuth(parsed) ? parsed : null;
      } catch {
        return null;
      }
    },
    save: auth => {
      const storage = getBrowserLocalStorage();
      if (!storage) return;
      storage.setItem(STORAGE_KEY, JSON.stringify(auth));
    },
    clear: () => {
      const storage = getBrowserLocalStorage();
      if (!storage) return;
      storage.removeItem(STORAGE_KEY);
    },
  };
};

export type AuthStoreListener = (auth: DashboardAuth | null) => void;

export const createAuthStore = (storage: AuthStorage = createLocalStorageAuthStorage()) => {
  let current: DashboardAuth | null = storage.load();
  const listeners = new Set<AuthStoreListener>();

  const notify = (): void => {
    for (const listener of listeners) listener(current);
  };

  return {
    getAuth: (): DashboardAuth | null => current,
    getToken: (): string | undefined => current?.token,
    setAuth: (auth: DashboardAuth): void => {
      current = auth;
      storage.save(auth);
      notify();
    },
    clearAuth: (): void => {
      current = null;
      storage.clear();
      notify();
    },
    subscribe: (listener: AuthStoreListener): (() => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};

// Browser singleton — rehydrates from localStorage on module load.
export const authStore = createAuthStore();
